import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@urbanx/agx.ui-components2'
import {
  clearTaskCollectionChanges,
  TaskCollectionChange,
  useTaskCollectionChanges,
} from 'hooks/useTaskCollectionChanges'
import { taskSearchQueryRoot } from './taskSearchQuery'

const taskCollectionChangeNotificationId = 'task-collection-change-notification'

export const useTaskCollectionChangeNotifier = () => {
  const knownTaskChanges = useRef(new Set<string>())
  const reconnectMayHaveMissedChanges = useRef(false)
  const queryClient = useQueryClient()
  const { pushToast, removeToast } = useToast()

  const refreshTasks = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: taskSearchQueryRoot })
    clearTaskCollectionChanges()
    knownTaskChanges.current.clear()
    reconnectMayHaveMissedChanges.current = false
    removeToast(taskCollectionChangeNotificationId)
  }, [queryClient, removeToast])

  const showTaskCollectionChange = useCallback(
    (change: TaskCollectionChange) => {
      if (change.reason === 'reconnected') {
        reconnectMayHaveMissedChanges.current = true
      } else {
        knownTaskChanges.current.add(change.taskId ?? change.key)
      }

      const numberOfKnownChanges = knownTaskChanges.current.size
      const message = reconnectMayHaveMissedChanges.current
        ? 'Task list updates may be available.'
        : `There ${numberOfKnownChanges === 1 ? 'is' : 'are'} ${numberOfKnownChanges} task list update${numberOfKnownChanges === 1 ? '' : 's'} available.`

      pushToast(
        {
          message,
          action: {
            label: 'Refresh',
            onClick: () => void refreshTasks(),
          },
        },
        taskCollectionChangeNotificationId
      )
    },
    [pushToast, refreshTasks]
  )

  useTaskCollectionChanges(showTaskCollectionChange)

  useEffect(
    () => () => removeToast(taskCollectionChangeNotificationId),
    [removeToast]
  )
}
